import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, MessageSquare, Send, Check, RotateCcw, X, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";

const FIELD_LABEL = { title: "Title", description: "Description", x_post: "X Post" };

// Per-media revision chat (Iter126C): every request → CURRENT vs PROPOSED; only Approve writes. Permanent rules are shown explicitly.
export const OwnerChat = ({ media, onWritten }) => {
  const [turns, setTurns] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [acting, setActing] = useState(null);
  const endRef = useRef(null);
  const id = media?.id;

  useEffect(() => {
    if (!id) return;
    let alive = true;
    api.get(`/owner/media/${id}/chat`).then(({ data }) => { if (alive) setTurns(data); }).catch(() => {});
    return () => { alive = false; };
  }, [id]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: "nearest" }); }, [turns.length]);

  const send = async (message, retryOf) => {
    if (!message.trim() || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/owner/media/${id}/chat`, { message, retry_of: retryOf || undefined });
      setTurns((t) => [...t.map((x) => (x.id === retryOf ? { ...x, status: "retried" } : x)), { id: `u-${Date.now()}`, role: "owner", message }, data]);
      setText("");
    } catch (e) { toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Chat failed"); }
    finally { setBusy(false); }
  };

  const approve = async (turn) => {
    setActing(turn.id);
    try {
      const { data } = await api.post(`/owner/media/${id}/chat/${turn.id}/approve`);
      setTurns((t) => t.map((x) => (x.id === turn.id ? { ...x, status: "approved" } : x)));
      onWritten?.(data.media);
      toast.success(`Saved ${data.written.map((f) => FIELD_LABEL[f]).join(", ")}${data.rule ? " · permanent rule saved" : ""}`);
    } catch (e) { toast.error(e?.response?.data?.detail?.code || "Approve failed"); }
    finally { setActing(null); }
  };
  const cancel = async (turn) => {
    setActing(turn.id);
    try { await api.post(`/owner/media/${id}/chat/${turn.id}/cancel`); setTurns((t) => t.map((x) => (x.id === turn.id ? { ...x, status: "cancelled" } : x))); }
    catch (e) { toast.error("Cancel failed"); }
    finally { setActing(null); }
  };
  const retry = (turn) => send(turn.proposal?.instruction || "try again", turn.id);

  return (
    <section className="rounded-xl border border-border bg-background/60 p-3 space-y-3" data-testid={`owner-card-chat-${id}`}>
      <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground inline-flex items-center gap-1.5"><MessageSquare className="w-3 h-3" /> Chat about this media</div>
      <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1" data-testid={`owner-chat-turns-${id}`}>
        {turns.length === 0 && <div className="text-xs text-muted-foreground">Try: “make this less robotic”, “shorten the title”, “keep the description but rewrite X”, “never say ‘both feet were planted’ again”.</div>}
        {turns.map((t) => (
          <div key={t.id} className={cn("text-sm", t.role === "owner" ? "text-right" : "")}>
            <div className={cn("inline-block rounded-2xl px-3 py-2 max-w-[95%] text-left", t.role === "owner" ? "bg-primary/15" : "bg-secondary/50")} data-testid={`owner-chat-turn-${t.id}`}>
              {t.message}
            </div>
            {t.proposal && <Proposal turn={t} busy={acting === t.id || busy} onApprove={() => approve(t)} onRetry={() => retry(t)} onCancel={() => cancel(t)} />}
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); send(text); }}>
        <input value={text} onChange={(e) => setText(e.target.value)} disabled={busy} placeholder="Ask for a change…"
          className="flex-1 h-11 rounded-full bg-background/80 border border-border px-4 text-sm" data-testid={`owner-chat-input-${id}`} />
        <Button type="submit" disabled={busy || !text.trim()} className="h-11 rounded-full px-4" data-testid={`owner-chat-send-${id}`}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </form>
    </section>
  );
};

const Proposal = ({ turn, busy, onApprove, onRetry, onCancel }) => {
  const p = turn.proposal;
  const open = turn.status === "proposed";
  return (
    <div className="mt-2 rounded-xl border border-primary/40 bg-card/70 p-3 space-y-3 text-left" data-testid={`owner-proposal-${turn.id}`} data-status={turn.status}>
      {p.scope === "permanent" && (
        <div className="text-[11px] text-amber-200 inline-flex items-start gap-1.5" data-testid={`owner-proposal-permanent-${turn.id}`}>
          <Sparkles className="w-3 h-3 mt-0.5 shrink-0" />
          <span>
            <span className="font-semibold">Permanent style rule</span> — nothing is saved until you press Approve. Exact rule{(p.permanent_rules || []).length > 1 ? "s" : ""} that will be stored:
            <ul className="list-disc ml-4 mt-1 space-y-0.5">{(p.permanent_rules && p.permanent_rules.length ? p.permanent_rules : [p.permanent_rule]).map((r, i) => <li key={i} data-testid={`owner-proposal-rule-${turn.id}-${i}`}>{r}</li>)}</ul>
          </span>
        </div>
      )}
      {p.fields.length === 0 && p.scope === "permanent" && <div className="text-xs text-muted-foreground" data-testid={`owner-proposal-rule-only-${turn.id}`}>No text changes for this media — Approve saves the rule for all future captions.</div>}
      {p.fields.map((f) => (
        <div key={f} className="space-y-1.5">
          <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">{FIELD_LABEL[f]}</div>
          <div className="grid gap-2 md:grid-cols-2">
            <div className="rounded-lg bg-background/70 border border-border p-2">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Current</div>
              <div className="text-xs whitespace-pre-wrap text-muted-foreground" data-testid={`owner-proposal-current-${f}-${turn.id}`}>{p.current[f] || "—"}</div>
            </div>
            <div className="rounded-lg bg-primary/10 border border-primary/40 p-2">
              <div className="text-[10px] uppercase tracking-wider text-primary mb-1">Proposed</div>
              <div className="text-xs whitespace-pre-wrap" data-testid={`owner-proposal-proposed-${f}-${turn.id}`}>{p.proposed[f]}</div>
            </div>
          </div>
        </div>
      ))}
      {open ? (
        <div className="flex gap-2">
          <Button type="button" onClick={onApprove} disabled={busy} className="h-10 rounded-full flex-1 font-semibold" data-testid={`owner-proposal-approve-${turn.id}`}><Check className="w-4 h-4 mr-1" /> Approve</Button>
          <Button type="button" variant="secondary" onClick={onRetry} disabled={busy} className="h-10 rounded-full" data-testid={`owner-proposal-retry-${turn.id}`}><RotateCcw className="w-4 h-4 mr-1" /> Try again</Button>
          <Button type="button" variant="ghost" onClick={onCancel} disabled={busy} className="h-10 rounded-full" data-testid={`owner-proposal-cancel-${turn.id}`}><X className="w-4 h-4 mr-1" /> Cancel</Button>
        </div>
      ) : (
        <div className="text-[11px] text-muted-foreground" data-testid={`owner-proposal-status-${turn.id}`}>{turn.status === "approved" ? "Approved — written to the media." : turn.status === "cancelled" ? "Cancelled — nothing written." : "Replaced by a new proposal."}</div>
      )}
    </div>
  );
};
